import {
  Flex,
  uniq,
  AssetIcon,
  getAssetSubType,
} from '@mss/mss-component-library';
import React from 'react';
import { TFunction } from 'i18next';

export const iconColumn = () => {
  return {
    dataIndex: '',
    key: '',
    width: 30,
    render: (record: any) => (
      <Flex h="center" v="center" style={{ maxWidth: 14, maxHeight: 14 }}>
        <AssetIcon asset={record.data || record} />
      </Flex>
    ),
  };
};

export const hostnameColumn = (
  t: TFunction,
  dataSource: string | string[] = 'name'
) => {
  return {
    title: t('Hostname'),
    dataIndex: dataSource,
    key: 'name',
    render: (hostname: any) => <span data-ta="Hostname">{hostname}</span>,
  };
};

const filterOrderByType = [
  'compute',
  'dcs_plc',
  'network',
  'field',
  'env',
  'security',
  'application',
  'control',
  'asset_management',
  'analyzer_management',
  'virtualization',
];

export const resourceColumn = (
  t: TFunction,
  data: any[],
  filteredValue?: string[],
  hideField?: boolean
) => {
  return {
    title: t('Asset/Application'),
    dataIndex: 'type',
    key: 'type',
    width: 200,
    sorter: (a: any, b: any) => {
      const aTranslated = t(a.type);
      const bTranslated = t(b.type);
      return aTranslated.localeCompare(bTranslated);
    },
    filteredValue,
    filters: uniq(data.map(x => x.type))
      .filter(x => !(x === 'field' && hideField))
      .sort(
        (a: any, b: any) =>
          filterOrderByType.indexOf(a) - filterOrderByType.indexOf(b)
      )
      .map(type => ({
        text: t(type),
        value: type,
      })),
    onFilter: (value: string, record: any) => {
      return record.type.indexOf(value) === 0;
    },
    render: (type: any) => <span data-ta="Type">{t(type)}</span>,
  };
};

export const typeColumn = (
  t: TFunction,
  data: any[],
  filteredValue?: string[]
) => {
  return {
    title: t('Type'),
    dataIndex: 'subType',
    key: 'subType',
    width: 200,
    render: (_: any, record: any) => {
      return t(record[getAssetSubType(record.type)]);
    },
    filterSearch: true,
    sorter: (a: any, b: any) =>
      a[getAssetSubType(a.type)].localeCompare(b[getAssetSubType(b.type)]),
    filteredValue,
    filters: uniq(
      data
        .sort(
          (a, b) =>
            filterOrderByType.indexOf(a.type) -
            filterOrderByType.indexOf(b.type)
        )
        .map(record => record[getAssetSubType(record.type)])
    ).map(subtype => {
      return {
        text: t(subtype),
        value: subtype,
      };
    }),
    onFilter: (value: string, record: any) => {
      return record[getAssetSubType(record.type)].indexOf(value) === 0;
    },
  };
};

export const roleColumn = (
  t: TFunction,
  data: any[],
  filteredValue?: string[]
) => {
  return {
    title: t('Role'),
    dataIndex: 'role',
    key: 'role',
    width: 100,
    filteredValue,
    filters: uniq(data.map(x => x.role)).map(role => ({
      text: role,
      value: role,
    })),
    onFilter: (value: string, record: any) => {
      return record.role.indexOf(value) === 0;
    },
    sorter: (a: any, b: any) => a.role.localeCompare(b.role),
  };
};

export const custodianColumn = (
  t: TFunction,
  dataSource: any[],
  filteredValue?: string[]
) => {
  return {
    title: t('Custodian'),
    dataIndex: 'custodian',
    key: 'custodian_id',
    width: 150,
    filters: Array.from(
      new Map(
        dataSource.map(item => [item.custodian.id, item.custodian])
      ).values()
    ).map((custodian: any) => ({ text: custodian.name, value: custodian.id })),
    filteredValue,
    onFilter: (value: string, record: any) => {
      return record.custodian?.id === value;
    },
    render: (x: any) => x.name || '',
    sorter: (a: any, b: any) =>
      (a.custodian.name || '').localeCompare(b.custodian.name || ''),
  };
};

export const locationColumn = (
  t: TFunction,
  dataSource: any[],
  filteredValue?: string[]
) => {
  return {
    title: t('Location'),
    dataIndex: 'location',
    key: 'location',
    width: 150,
    filteredValue,
    filters: uniq(dataSource.map(x => x.location)).map(location => ({
      text: location,
      value: location,
    })),
    onFilter: (value: string, record: any) => {
      return record.location.indexOf(value) === 0;
    },
    sorter: (a: any, b: any) => a.location.localeCompare(b.location),
  };
};
